/**
 * Composable for SVG rendering utilities.
 *
 * Provides helpers to:
 * - Create SVG text elements from design items
 * - Measure text dimensions via hidden cache element
 * - Build export-ready SVG composites
 */

import { measureNaturalText as measureNaturalTextDom, measureText as measureTextDom, measureTextBox as measureTextBoxDom, measureTextLayerBounds as measureTextLayerBoundsDom } from '@core/textMeasurement';

export function useSvgRenderer() {
  /** Create an SVG <text> element string from item properties */
  function createTextSvg(item: {
    text: string;
    fontFamily?: string;
    fontSize?: number;
    fontWeight?: string;
    fontStyle?: string;
    color?: string;
    textAlign?: string;
    outlineColor?: string;
    outlineWidth?: number;
    lineHeight?: number;
    letterSpacing?: number;
    width?: number;
    height?: number;
  }, opts?: { fontFaceCss?: string }): string {
    const family = item.fontFamily ?? 'Arial';
    const size = item.fontSize ?? 24;
    const weight = item.fontWeight ?? 'normal';
    const style = item.fontStyle ?? 'normal';
    const fill = item.color ?? '#000';
    const align = item.textAlign ?? 'center';
    const outColor = item.outlineColor ?? 'none';
    const outWidth = item.outlineWidth ?? 0;

    const w = item.width ?? 200;
    const h = item.height ?? 50;

    // Lay the lines out like the canvas does (measureTextLayerBounds: 4px 8px padding, CSS
    // line-height, block centred in the box). The first baseline used to sit at y = fontSize,
    // so text hugged the top of its box. Baselines are plain numbers (line centre + 0.35em,
    // about (ascent - descent) / 2 for common fonts) rather than dominant-baseline, which
    // vector tools reading the production SVG often ignore.
    const padX = 8;
    const anchor = align === 'left' ? 'start' : align === 'right' ? 'end' : 'middle';
    const x = align === 'left' ? padX : align === 'right' ? w - padX : w / 2;
    const lineHeight = Number(item.lineHeight) > 0 ? Number(item.lineHeight) : 1.2;
    const lineBox = size * lineHeight;
    const letterSpacing = Number(item.letterSpacing) || 0;

    const lines = String(item.text).split('\n');
    const blockTop = (h - lines.length * lineBox) / 2;
    const tspans = lines
      .map((line, i) => {
        const baseline = blockTop + lineBox * (i + 0.5) + size * 0.35;
        return `<tspan x="${x}" y="${Math.round(baseline * 100) / 100}">${escapeXml(line)}</tspan>`;
      })
      .join('');
    const spacingAttr = letterSpacing ? ` letter-spacing="${letterSpacing}"` : '';

    const strokeAttr =
      outWidth > 0 ? `stroke="${escapeAttr(outColor)}" stroke-width="${outWidth}"` : '';

    const fontFaceCss = opts?.fontFaceCss ? `<style>${opts.fontFaceCss}</style>` : '';

    return [
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}">`,
      fontFaceCss,
      `<text font-family="${escapeAttr(family)}" font-size="${size}" font-weight="${weight}" font-style="${style}" fill="${escapeAttr(fill)}" text-anchor="${anchor}"${spacingAttr} ${strokeAttr}>`,
      tspans,
      `</text>`,
      `</svg>`,
    ].join('');
  }

  function measureText(
    text: string,
    fontFamily: string,
    fontSize: number,
    fontWeight = 'normal',
    fontStyle = 'normal',
  ): { width: number; height: number } {
    return measureTextDom(text, fontFamily, fontSize, fontWeight, fontStyle);
  }

  function measureNaturalText(
    text: string,
    fontFamily: string,
    fontSize: number,
    opts?: {
      fontWeight?: string;
      fontStyle?: string;
      lineHeight?: number;
      letterSpacing?: number;
    },
  ): { width: number; height: number } {
    return measureNaturalTextDom(text, fontFamily, fontSize, opts);
  }

  function measureTextBox(
    text: string,
    fontFamily: string,
    fontSize: number,
    boxWidthPx: number,
    opts?: {
      fontWeight?: string;
      fontStyle?: string;
      lineHeight?: number;
      letterSpacing?: number;
    },
  ): { width: number; height: number } {
    return measureTextBoxDom(text, fontFamily, fontSize, boxWidthPx, opts);
  }

  function measureTextLayerBounds(
    text: string,
    fontFamily: string,
    fontSize: number,
    opts?: {
      fontWeight?: string;
      fontStyle?: string;
      lineHeight?: number;
      letterSpacing?: number;
      textDecoration?: string;
      textAlign?: string;
    },
  ): { width: number; height: number } {
    return measureTextLayerBoundsDom(text, fontFamily, fontSize, opts);
  }

  /** Escape XML special characters for SVG content */
  function escapeXml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /** Escape for attribute values */
  function escapeAttr(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  return {
    createTextSvg,
    measureText,
    measureNaturalText,
    measureTextLayerBounds,
    measureTextBox,
    escapeXml,
    escapeAttr,
  };
}
